import { motion } from "framer-motion";
import { Megaphone, X } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";

// Centered, scrollable announcement dialog with images shown at the bottom.
// Shared by the resident Home and the manager Home.
export function AnnouncementDetail({ a, onClose }) {
  const imgs = (a.images || []).filter((i) => i.url);
  return (
    <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        className="relative flex max-h-[82vh] w-full max-w-lg flex-col rounded-2xl border border-white/10 bg-ink-900 shadow-2xl" data-testid="ann-detail">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <div className="flex items-center gap-1.5">
            <Megaphone size={14} weight="fill" className="text-neon-lime" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-neon-lime">{a.tag}</span>
          </div>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <h3 className="font-display text-xl font-bold leading-tight text-white">{a.title}</h3>
          {a.body && <p className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed text-white/70">{a.body}</p>}
          {a.createdByName && <p className="mt-3 text-[11px] text-white/45">Posted by {a.createdByName}</p>}
          <p className="mt-1 text-[10px] uppercase tracking-wider text-white/40">{new Date(a.createdAt).toLocaleString()}</p>
          {imgs.length > 0 && (
            <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/50">Attachments</p>
              {imgs.map((im, i) => (
                <a key={i} href={im.url} target="_blank" rel="noreferrer" className="block">
                  <img src={im.url} alt="" className="w-full rounded-xl border border-white/10 object-cover" />
                </a>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export function AnnouncementsAllSheet({ items, onClose, onOpen }) {
  const { t } = useI18n();
  return (
    <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <motion.div initial={{ scale: 0.96, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.96, opacity: 0 }}
        className="relative flex max-h-[82vh] w-full max-w-lg flex-col rounded-2xl border border-white/10 bg-ink-900 shadow-2xl" data-testid="ann-all-sheet">
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
          <h3 className="font-display text-lg font-bold text-white">{t("ann.allTitle")}</h3>
          <button onClick={onClose} className="rounded-full bg-white/5 p-1.5 text-white/60"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-2 overflow-y-auto p-4">
          {items.length === 0 ? <p className="text-[13px] text-white/55">{t("ann.empty")}</p> : items.map((a) => (
            <button key={a.id} onClick={() => onOpen(a)} className="w-full rounded-2xl border border-white/10 bg-white/[0.03] p-3 text-left hover:border-white/20" data-testid={`ann-all-${a.id}`}>
              <div className="mb-1 flex items-center gap-1.5">
                <Megaphone size={11} weight="fill" className="text-neon-lime" />
                <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-neon-lime">{a.tag}</span>
                <span className="ml-auto text-[10px] text-white/40">{new Date(a.createdAt).toLocaleDateString()}</span>
              </div>
              <p className="font-display text-[14px] font-semibold text-white">{a.title}</p>
              <p className="line-clamp-1 text-[12px] text-white/55">{a.body}</p>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
